import { DateTime } from 'luxon';
import type { Ratings } from '@benzinga/calendar-manager';
import { isoToMillis } from '../../components/Quote/formatters';

const STRONG_BUY_SET = new Set(['Strong Buy', 'Conviction Buy', 'Market Outperform']);
const BUY_SET = new Set(['Buy', 'Outperform', 'Overweight']);
const HOLD_SET = new Set(['Hold', 'Neutral', 'Market Perform', 'Sector Perform', 'Equal Weight', 'In-Line']);
const SELL_SET = new Set(['Sell', 'Underperform', 'Underweight']);
const STRONG_SELL_SET = new Set(['Strong Sell']);

export type RatingCategory = 'strongBuy' | 'buy' | 'hold' | 'sell' | 'strongSell';

export type RatingTrendSnapshot = {
  label: string;
  strongBuy: number;
  buy: number;
  hold: number;
  sell: number;
  strongSell: number;
};

export const classifyRating = (rating: string): RatingCategory | null => {
  if (STRONG_BUY_SET.has(rating)) return 'strongBuy';
  if (BUY_SET.has(rating)) return 'buy';
  if (HOLD_SET.has(rating)) return 'hold';
  if (SELL_SET.has(rating)) return 'sell';
  if (STRONG_SELL_SET.has(rating)) return 'strongSell';
  return null;
};

export const buildRatingTrendSnapshots = (ratings: Ratings[]): RatingTrendSnapshot[] => {
  const valid = ratings.filter(rating => rating.date && rating.rating_current);
  if (!valid.length) return [];
  const ascending = [...valid].sort((left, right) => isoToMillis(left.date) - isoToMillis(right.date));

  const months = [...new Set(ascending.map(rating => DateTime.fromISO(rating.date).toFormat('yyyy-LL')))]
    .sort()
    .slice(-9)
    .map(monthKey => DateTime.fromFormat(monthKey, 'yyyy-LL'));

  return months.map(monthDate => {
    const monthEnd = monthDate.endOf('month');
    const label = `${monthDate.toFormat('LLL')} '${monthDate.toFormat('yy')}`;

    const latestByFirm: Record<string, string> = {};
    for (const rating of ascending) {
      if (!rating.analyst || !rating.rating_current) continue;
      if (DateTime.fromISO(rating.date) <= monthEnd) {
        latestByFirm[rating.analyst] = rating.rating_current;
      }
    }

    const counts = { strongBuy: 0, buy: 0, hold: 0, sell: 0, strongSell: 0 };
    for (const rating of Object.values(latestByFirm)) {
      const category = classifyRating(rating);
      if (category) counts[category]++;
    }

    return { label, ...counts };
  });
};
